import React from 'react';
import { GraduationCap, Clock, PauseCircle } from 'lucide-react';

/**
 * Deux notions volontairement distinctes (style + couleur), a ne jamais confondre :
 *
 *  - COMPTE (administratif, source `learners.status`) :
 *      pilule PLEINE arrondie (rounded-full) avec un point de tete.
 *      Valeurs : Actif / Desactive. (jamais « Inactif », jamais « En attente »)
 *
 *  - SUIVI (engagement, calcule depuis enrollments + course_progress) :
 *      chip OUTLINE (rounded-lg) avec une icone de tete.
 *      Valeurs : En formation / En retard depuis X j / Pas demarre depuis X j.
 *      N'emploie JAMAIS « Actif » ni « Inactif ».
 *      Si le compte est desactive, le Suivi affiche « — ».
 */

export type TrackingAlert = 'delayed' | 'inactive' | null | undefined;

const formatDays = (n: number) => `${n} jour${n > 1 ? 's' : ''}`;

// ── COMPTE (statut administratif) ── pilule pleine + point ──
export const AccountBadge: React.FC<{ status: string }> = ({ status }) => {
  const map: Record<string, { label: string; cls: string }> = {
    active:   { label: 'Actif',     cls: 'bg-mint-container text-mint' },
    inactive: { label: 'Désactivé', cls: 'bg-cream-high text-ink-soft' },
  };
  // Toute autre valeur (ou inconnue) est traitee comme « Actif » par defaut.
  const entry = map[status] || map.active;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-full ${entry.cls}`}>
      <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" />
      {entry.label}
    </span>
  );
};

// ── SUIVI (engagement) ── chip outline + icone ──
export const TrackingBadge: React.FC<{
  alert?: TrackingAlert;
  daysSince?: number;
  accountStatus?: string;
  compact?: boolean;
  wrap?: boolean;
}> = ({ alert, daysSince, accountStatus, compact, wrap }) => {
  // Un compte desactive ne suit pas de formation.
  if (accountStatus === 'inactive') {
    return <span className="text-sm text-ink-soft/50">—</span>;
  }

  const suffix = typeof daysSince === 'number'
    ? (compact ? ` depuis ${daysSince} j` : ` depuis ${formatDays(daysSince)}`)
    : '';

  // Par defaut le libelle ne se coupe pas (whitespace-nowrap). Dans une colonne a
  // largeur contrainte (table-fixed), wrap=true autorise le passage sur deux lignes
  // pour rester dans la cellule sans declencher de defilement horizontal.
  const wrapCls = wrap ? 'whitespace-normal' : 'whitespace-nowrap';

  if (alert === 'inactive') {
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-lg border border-coral/40 text-coral bg-transparent ${wrapCls}`}>
        <PauseCircle className="w-3.5 h-3.5 flex-shrink-0" />
        Pas démarré{suffix}
      </span>
    );
  }

  if (alert === 'delayed') {
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-lg border border-gold/50 text-gold bg-transparent ${wrapCls}`}>
        <Clock className="w-3.5 h-3.5 flex-shrink-0" />
        En retard{suffix}
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-lg border border-mint/40 text-mint bg-transparent whitespace-nowrap">
      <GraduationCap className="w-3.5 h-3.5 flex-shrink-0" />
      En formation
    </span>
  );
};
